import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  Briefcase,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileCheck2,
  Filter,
  GraduationCap,
  Info,
  Loader2,
  Mail,
  MapPin,
  MessageSquareText,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  ThumbsDown,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import useJobStore from "../../store/jobStore";
import MatchIntelligencePanel from "../../components/matches/MatchIntelligencePanel";
import { formatCompensationRange } from "../../utils/compensation";

const inputClass =
  "h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

const asArray = (value) =>
  !value ? [] : Array.isArray(value) ? value : [value];
const score = (value) =>
  Number.isFinite(Number(value))
    ? Math.max(0, Math.min(100, Math.round(Number(value))))
    : null;
const labelize = (value) =>
  String(value || "Not set")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
const candidateName = (match) =>
  match?.candidate?.fullName ||
  [match?.candidate?.firstName, match?.candidate?.lastName]
    .filter(Boolean)
    .join(" ") ||
  "Candidate";
const initials = (name) =>
  String(name || "Candidate")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
const matchKey = (match) => String(match?.matchId || "");

const factText = (item) => {
  if (!item) return "";
  const text =
    typeof item === "string"
      ? item
      : item.explanation || item.summary || item.label || item.key || "";
  return String(text)
    .replace(
      /^Missing required skill:\s*/i,
      "No explicit evidence found for required skill: "
    )
    .replace(
      /^Missing preferred skill:\s*/i,
      "No explicit evidence found for preferred skill: "
    )
    .replace(
      /^Missing required evidence:\s*/i,
      "No explicit evidence found for: "
    );
};

const dimensionLabel = (key) =>
  ({
    skills: "Skills",
    experience: "Experience",
    education: "Education",
    location: "Location",
    salary: "Compensation",
    culture_fit: "Work style",
    micro_points: "Role checks",
  }[key] || labelize(key));

const toneForScore = (value) => {
  const numeric = score(value) || 0;
  if (numeric >= 80) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (numeric >= 65) return "border-sky-200 bg-sky-50 text-sky-700";
  if (numeric >= 50) return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
};

const confidenceTone = (level) =>
  ({
    high: "border-emerald-200 bg-emerald-50 text-emerald-700",
    moderate: "border-sky-200 bg-sky-50 text-sky-700",
    limited: "border-amber-200 bg-amber-50 text-amber-800",
    not_established: "border-slate-200 bg-slate-50 text-slate-600",
  }[level] || "border-slate-200 bg-slate-50 text-slate-600");

const statusLabel = (match) => {
  if (match?.employerAction === "rejected") return "Passed";
  if (
    match?.matchStatus === "mutual" ||
    (match?.employerAction === "interested" &&
      match?.candidateAction === "interested")
  )
    return "Mutual interest";
  if (match?.candidateAction === "interested") return "Candidate interested";
  if (match?.employerAction === "interested") return "Shortlisted";
  return labelize(match?.matchStatus || "Awaiting review");
};

const isMutualMatch = (match) =>
  match?.matchStatus === "mutual" ||
  (match?.employerAction === "interested" &&
    match?.candidateAction === "interested");

const educationFor = (match) =>
  asArray(match?.insights?.candidateEvidence?.education).length
    ? asArray(match.insights.candidateEvidence.education)
    : asArray(match?.candidate?.education);

const roleFor = (match) =>
  match?.candidate?.currentRole ||
  match?.insights?.candidateEvidence?.currentRole ||
  {};

const SummaryMetric = ({ icon: Icon, label, value, detail }) => (
  <div className="min-w-0 border-l border-slate-200 pl-4 first:border-l-0 first:pl-0">
    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
      <Icon size={14} /> {label}
    </div>
    <div className="mt-1 text-xl font-semibold text-[#0b1f3a]">{value}</div>
    <div className="mt-0.5 truncate text-xs text-slate-400">{detail}</div>
  </div>
);

const EmptyState = ({ filtered = false }) => (
  <div className="flex min-h-[340px] flex-col items-center justify-center px-6 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-500">
      {filtered ? <Filter size={20} /> : <UsersRound size={20} />}
    </div>
    <h2 className="mt-4 text-lg font-semibold text-[#0b1f3a]">
      {filtered ? "No matches meet these filters" : "No candidate matches yet"}
    </h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
      {filtered
        ? "Clear or broaden the filters to return more candidates."
        : "Matches will appear here when eligible candidate evidence aligns with this role."}
    </p>
  </div>
);

const MatchSkeleton = () => (
  <div
    className="divide-y divide-slate-100"
    aria-label="Loading candidate matches"
  >
    {[0, 1, 2].map((item) => (
      <div key={item} className="animate-pulse p-5">
        <div className="flex gap-4">
          <div className="h-10 w-10 rounded-md bg-slate-100" />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-4 w-44 rounded bg-slate-100" />
            <div className="h-3 w-64 max-w-full rounded bg-slate-100" />
            <div className="flex gap-2">
              <div className="h-7 w-24 rounded bg-slate-100" />
              <div className="h-7 w-28 rounded bg-slate-100" />
            </div>
          </div>
          <div className="h-10 w-20 rounded bg-slate-100" />
        </div>
      </div>
    ))}
  </div>
);

const MatchRow = ({ match, selected, onSelect, onReview, onAction }) => {
  const candidate = match.candidate || {};
  const role = roleFor(match);
  const support = match.decisionSupport || {};
  const dimensions = match.scoreBreakdown?.dimensions || {};
  const skillsScore = score(dimensions.skills);
  const experienceScore = score(dimensions.experience);
  const primaryConcern =
    asArray(support.concerns)[0] ||
    asArray(support.missingOrUnverified)[0] ||
    "No material concern recorded.";
  const salary = candidate.salaryExpectations || {};
  const salaryKnown =
    salary.min !== null &&
    salary.min !== undefined &&
    salary.max !== null &&
    salary.max !== undefined;

  return (
    <article className="group grid gap-4 p-4 transition hover:bg-slate-50/70 sm:p-5 xl:grid-cols-[28px_minmax(240px,1.1fr)_minmax(190px,.8fr)_minmax(230px,1fr)_auto] xl:items-center">
      <label
        className="flex h-8 items-center xl:self-start xl:pt-1"
        title={`Select ${candidateName(match)}`}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(match.matchId)}
          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
        />
      </label>

      <button
        type="button"
        onClick={() => onReview(match)}
        className="min-w-0 text-left"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#0b1f3a] text-xs font-semibold text-white">
            {initials(candidateName(match))}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-[#0b1f3a]">
                {candidateName(match)}
              </h3>
              {candidate.profile?.isVerified && (
                <BadgeCheck
                  size={15}
                  className="text-sky-600"
                  aria-label="Verified profile"
                />
              )}
            </div>
            <p className="mt-1 truncate text-sm text-slate-600">
              {role.job_title || role.jobTitle || candidate.experienceLevel
                ? `${
                    role.job_title ||
                    role.jobTitle ||
                    labelize(candidate.experienceLevel)
                  }${
                    role.company_name || role.companyName
                      ? ` at ${role.company_name || role.companyName}`
                      : ""
                  }`
                : "Role history available in profile"}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} />{" "}
                {candidate.location || "Location not provided"}
              </span>
              <span className="inline-flex items-center gap-1">
                <Briefcase size={12} />{" "}
                {candidate.experienceYears ?? "Unconfirmed"} yrs
              </span>
            </div>
          </div>
        </div>
      </button>

      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold text-[#0b1f3a]">
            {score(match.overallMatchScore) ?? "-"}%
          </span>
          <span className="text-xs font-medium text-slate-500">
            assistive score
          </span>
        </div>
        <p className="mt-1 text-xs font-medium text-slate-700">
          {support.fitLabel || `Band ${match.matchBand || "-"}`}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {skillsScore !== null && (
            <span
              className={`rounded-md border px-2 py-1 text-[11px] font-medium ${toneForScore(
                skillsScore
              )}`}
            >
              Skills {skillsScore}%
            </span>
          )}
          {experienceScore !== null && (
            <span
              className={`rounded-md border px-2 py-1 text-[11px] font-medium ${toneForScore(
                experienceScore
              )}`}
            >
              Experience {experienceScore}%
            </span>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <span
            className={`rounded-md border px-2 py-1 text-[11px] font-medium ${confidenceTone(
              support.confidenceLevel
            )}`}
          >
            {labelize(support.confidenceLevel || "Not established")} confidence
          </span>
          <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600">
            {statusLabel(match)}
          </span>
        </div>
        <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-600">
          {factText(primaryConcern)}
        </p>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400">
          <span>
            {salaryKnown
              ? formatCompensationRange(salary)
              : "Compensation unassessed"}
          </span>
          <span>
            {candidate.preferences?.workAuthorizationStatus
              ? labelize(candidate.preferences.workAuthorizationStatus)
              : "Work authorization unknown"}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 xl:pl-2">
        {!match.employerAction && (
          <button
            type="button"
            onClick={() => onAction(match, "interested")}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-[#0b1f3a] px-3 text-xs font-medium text-white hover:bg-[#12345c]"
          >
            <Check size={14} /> Shortlist
          </button>
        )}
        <button
          type="button"
          onClick={() => onReview(match)}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          Review <ChevronRight size={14} />
        </button>
      </div>
    </article>
  );
};

const DecisionList = ({ title, icon: Icon, items, tone = "slate", empty }) => {
  const rows = asArray(items).map(factText).filter(Boolean);
  const tones = {
    emerald: "border-emerald-100 bg-emerald-50/60 text-emerald-800",
    amber: "border-amber-100 bg-amber-50/60 text-amber-900",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <section className={`rounded-md border p-4 ${tones[tone]}`}>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase text-current">
        <Icon size={14} /> {title}
      </h3>
      {rows.length ? (
        <ul className="mt-3 space-y-2">
          {rows.map((item, index) => (
            <li
              key={`${item}-${index}`}
              className="flex gap-2 text-sm leading-6"
            >
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-60" />
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm opacity-70">{empty}</p>
      )}
    </section>
  );
};

const ScoreGrid = ({ match }) => {
  const dimensions = match?.scoreBreakdown?.dimensions || {};
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {Object.entries(dimensions).map(([key, value]) => {
        const numeric = score(value);
        return (
          <div key={key} className="rounded-md border border-slate-200 p-3">
            <div className="text-xs text-slate-500">{dimensionLabel(key)}</div>
            <div className="mt-1 text-lg font-semibold text-[#0b1f3a]">
              {numeric === null ? "Not scored" : `${numeric}%`}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EducationPanel = ({ match }) => {
  const education = educationFor(match);
  if (!education.length)
    return (
      <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-500">
        No education evidence is available.
      </p>
    );

  return (
    <div className="space-y-4">
      {education.map((item, index) => {
        const verification = item.verificationSummary || {};
        const statements = asArray(verification.employerSummary);
        return (
          <section
            key={`${item.institution || item.institution_name}-${index}`}
            className="rounded-md border border-slate-200 p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-sky-50 text-sky-700">
                <GraduationCap size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-[#0b1f3a]">
                  {item.institution ||
                    item.institution_name ||
                    "Institution not stated"}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {[
                    item.degree || item.degree_type,
                    item.field || item.field_of_study,
                  ]
                    .filter(Boolean)
                    .join(" in ") || "Qualification details not stated"}
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {(statements.length
                ? statements
                : [
                    "Institution verification has not been completed.",
                    "Candidate attendance not independently verified.",
                  ]
              ).map((statement) => {
                const positive =
                  /source found|supported by public evidence/i.test(statement);
                return (
                  <div
                    key={statement}
                    className={`flex gap-2 rounded-md border px-3 py-2 text-xs leading-5 ${
                      positive
                        ? "border-emerald-100 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    {positive ? (
                      <FileCheck2 size={14} className="mt-0.5 shrink-0" />
                    ) : (
                      <Info size={14} className="mt-0.5 shrink-0" />
                    )}
                    {statement}
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Institution or programme recognition does not independently verify
              that the candidate attended or graduated.
            </p>
          </section>
        );
      })}
    </div>
  );
};

const RequirementsPanel = ({ match }) => {
  const matched = asArray(
    match?.insights?.requirementEvidence || match?.explanation?.matchedFacts
  );
  const gaps = asArray(
    match?.decisionSupport?.concerns || match?.explanation?.gaps
  );
  const checks = asArray(
    match?.insights?.evidenceSignals || match?.microPoints
  ).filter((item) => !item.excludedFromDecisionSupport);
  return (
    <div className="space-y-4">
      <DecisionList
        title="Supported requirements"
        icon={Check}
        items={matched}
        tone="emerald"
        empty="No direct requirement evidence has been recorded."
      />
      <DecisionList
        title="Review required"
        icon={AlertTriangle}
        items={gaps}
        tone="amber"
        empty="No material requirement gaps have been recorded."
      />
      {checks.length > 0 && (
        <section>
          <h3 className="mb-3 text-xs font-semibold uppercase text-slate-500">
            Role-specific checks
          </h3>
          <div className="space-y-2">
            {checks.map((item, index) => (
              <div
                key={`${item.key}-${index}`}
                className="flex items-start justify-between gap-4 rounded-md border border-slate-200 p-3"
              >
                <div>
                  <div className="text-sm font-medium text-[#0b1f3a]">
                    {item.label || labelize(item.key)}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    {factText(item)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium ${
                    item.passed
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  {item.passed ? "Supported" : "Review"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const ActivityPanel = ({ match }) => {
  const lifecycle = match?.lifecycle || {};
  const interviews = asArray(match?.interviews);
  const rows = [
    ["Match created", match?.actionTimestamps?.createdAt],
    ["Employer action", match?.actionTimestamps?.employerActionAt],
    ["Candidate action", match?.actionTimestamps?.candidateActionAt],
    ["Next step due", lifecycle.nextStepDueAt],
  ].filter(([, value]) => value);
  return (
    <div className="space-y-4">
      <section className="rounded-md border border-slate-200 p-4">
        <h3 className="text-xs font-semibold uppercase text-slate-500">
          Review history
        </h3>
        {rows.length ? (
          <div className="mt-3 divide-y divide-slate-100">
            {rows.map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between gap-4 py-3 text-sm"
              >
                <span className="text-slate-600">{label}</span>
                <span className="font-medium text-[#0b1f3a]">
                  {new Intl.DateTimeFormat("en-GB", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(value))}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500">
            No actions have been recorded yet.
          </p>
        )}
      </section>
      <section className="rounded-md border border-slate-200 p-4">
        <h3 className="text-xs font-semibold uppercase text-slate-500">
          Interviews
        </h3>
        <p className="mt-3 text-sm text-slate-600">
          {interviews.length
            ? `${interviews.length} interview record${
                interviews.length === 1 ? "" : "s"
              } available.`
            : "No interviews scheduled."}
        </p>
      </section>
    </div>
  );
};

const MatchDrawer = ({
  match,
  loading,
  error,
  onClose,
  onAction,
  onProfile,
  onInterview,
  onOffer,
}) => {
  const [tab, setTab] = useState("summary");
  useEffect(() => setTab("summary"), [match?.matchId]);
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!match) return null;

  const support = match.decisionSupport || {};
  const tabs = [
    ["summary", "Summary"],
    ["requirements", "Requirements"],
    ["education", "Education"],
    ["profile", "Full evidence"],
    ["activity", "Activity"],
  ];

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-950/35"
      role="dialog"
      aria-modal="true"
      aria-label={`Review ${candidateName(match)}`}
    >
      <button
        type="button"
        aria-label="Close candidate review"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full flex-col bg-white shadow-2xl sm:max-w-3xl">
        <header className="border-b border-slate-200 px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#0b1f3a] text-sm font-semibold text-white">
                {initials(candidateName(match))}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-[#0b1f3a]">
                  {candidateName(match)}
                </h2>
                <p className="mt-1 truncate text-sm text-slate-500">
                  {roleFor(match).job_title ||
                    roleFor(match).jobTitle ||
                    labelize(match.candidate?.experienceLevel)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-2xl font-semibold text-[#0b1f3a]">
              {score(match.overallMatchScore) ?? "-"}%
            </span>
            <span className="text-xs text-slate-500">assistive score</span>
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium ${confidenceTone(
                support.confidenceLevel
              )}`}
            >
              {labelize(support.confidenceLevel || "Not established")}{" "}
              confidence
            </span>
            <span className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600">
              {statusLabel(match)}
            </span>
          </div>
        </header>

        <nav
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:px-6"
          aria-label="Candidate match details"
        >
          {tabs.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`h-11 whitespace-nowrap border-b-2 px-3 text-xs font-medium ${
                tab === value
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex min-h-[260px] items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 size={18} className="animate-spin" /> Loading complete
              evidence
            </div>
          ) : error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          ) : (
            <>
              {tab === "summary" && (
                <div className="space-y-4">
                  {support.calibration?.requiresManualReview && (
                    <div className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <strong>Manual calibration review recommended.</strong>
                        <div>
                          The role definition has limited calibration quality.
                          Treat the score as supporting evidence, not an
                          automatic ranking.
                        </div>
                      </div>
                    </div>
                  )}
                  <ScoreGrid match={match} />
                  <div className="grid gap-4 lg:grid-cols-2">
                    <DecisionList
                      title="Why shortlist"
                      icon={Check}
                      items={
                        support.whyShortlist || match.explanation?.strengths
                      }
                      tone="emerald"
                      empty="No shortlist evidence has been summarized."
                    />
                    <DecisionList
                      title="Concerns"
                      icon={AlertTriangle}
                      items={support.concerns || match.explanation?.gaps}
                      tone="amber"
                      empty="No material concern has been summarized."
                    />
                  </div>
                  <DecisionList
                    title="Missing or unverified"
                    icon={Info}
                    items={support.missingOrUnverified}
                    empty="No additional missing information has been flagged."
                  />
                </div>
              )}
              {tab === "requirements" && <RequirementsPanel match={match} />}
              {tab === "education" && <EducationPanel match={match} />}
              {tab === "profile" && <MatchIntelligencePanel match={match} />}
              {tab === "activity" && <ActivityPanel match={match} />}
            </>
          )}
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-white p-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onProfile(match)}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <UserRound size={16} /> Candidate profile{" "}
              <ExternalLink size={13} />
            </button>
            <div className="flex flex-wrap justify-end gap-2">
              {match.employerAction !== "rejected" && (
                <button
                  type="button"
                  onClick={() => onAction(match, "not_interested")}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  <ThumbsDown size={15} /> Pass
                </button>
              )}
              {isMutualMatch(match) && match.candidate?.contact?.visible && (
                <button
                  type="button"
                  onClick={() => onAction(match, "contacted")}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Mail size={15} /> Contact
                </button>
              )}
              {isMutualMatch(match) && (
                <button
                  type="button"
                  onClick={() => onInterview(match)}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-sky-200 px-3 text-sm font-medium text-sky-700 hover:bg-sky-50"
                >
                  <CalendarClock size={15} /> Interview
                </button>
              )}
              {isMutualMatch(match) && (
                <button
                  type="button"
                  onClick={() => onOffer(match)}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-emerald-200 px-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                >
                  <FileCheck2 size={15} /> Offer
                </button>
              )}
              {!match.employerAction && (
                <button
                  type="button"
                  onClick={() => onAction(match, "interested")}
                  className="inline-flex h-10 items-center gap-2 rounded-md bg-[#0b1f3a] px-4 text-sm font-medium text-white hover:bg-[#12345c]"
                >
                  <Send size={15} /> Express interest
                </button>
              )}
            </div>
          </div>
        </footer>
      </aside>
    </div>
  );
};

const CompareDialog = ({ matches, onClose, onReview }) => {
  if (!matches.length) return null;
  const dimensionValue = (match, key) => {
    const value = score(match.scoreBreakdown?.dimensions?.[key]);
    return value === null ? "Not scored" : `${value}%`;
  };
  const rows = [
    ["Overall fit", (match) => `${score(match.overallMatchScore) ?? "-"}%`],
    [
      "Confidence",
      (match) =>
        `${labelize(
          match.decisionSupport?.confidenceLevel || "Not established"
        )}`,
    ],
    ["Skills", (match) => dimensionValue(match, "skills")],
    ["Experience", (match) => dimensionValue(match, "experience")],
    ["Education", (match) => dimensionValue(match, "education")],
    ["Location", (match) => dimensionValue(match, "location")],
    ["Compensation", (match) => dimensionValue(match, "salary")],
    [
      "Current role",
      (match) =>
        roleFor(match).job_title || roleFor(match).jobTitle || "Not provided",
    ],
    [
      "Work authorization",
      (match) =>
        labelize(
          match.candidate?.preferences?.workAuthorizationStatus ||
            "Not provided"
        ),
    ],
    [
      "Primary concern",
      (match) =>
        factText(
          asArray(match.decisionSupport?.concerns)[0] ||
            asArray(match.decisionSupport?.missingOrUnverified)[0]
        ) || "None recorded",
    ],
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Compare selected candidates"
    >
      <section className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-md bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-4 sm:px-6">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0b1f3a]">
              <UsersRound size={18} /> Compare candidates
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Job-relevant evidence is shown side by side. Scores remain
              assistive.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            title="Close comparison"
          >
            <X size={18} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#e2e8f0]">
              <tr>
                <th className="w-40 p-4 text-xs font-semibold uppercase text-slate-500">
                  Evidence
                </th>
                {matches.map((match) => (
                  <th key={match.matchId} className="min-w-[210px] p-4">
                    <div className="font-semibold text-[#0b1f3a]">
                      {candidateName(match)}
                    </div>
                    <div className="mt-1 text-xs font-normal text-slate-500">
                      {roleFor(match).job_title ||
                        roleFor(match).jobTitle ||
                        labelize(match.candidate?.experienceLevel)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, valueFor]) => (
                <tr key={label} className="border-t border-slate-100 align-top">
                  <th className="bg-slate-50/70 p-4 text-xs font-medium text-slate-500">
                    {label}
                  </th>
                  {matches.map((match) => (
                    <td
                      key={`${label}-${match.matchId}`}
                      className={`p-4 leading-5 ${
                        label === "Primary concern"
                          ? "text-amber-800"
                          : "text-slate-700"
                      }`}
                    >
                      {valueFor(match)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">
          {matches.map((match) => (
            <button
              key={match.matchId}
              type="button"
              onClick={() => onReview(match)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Review {candidateName(match).split(" ")[0]}{" "}
              <ChevronRight size={13} />
            </button>
          ))}
        </footer>
      </section>
    </div>
  );
};

const ActionDialog = ({ draft, onChange, onClose, onSubmit, submitting }) => {
  if (!draft) return null;
  const isPass = draft.action === "not_interested";
  const isContact = draft.action === "contacted";
  const update = (field, value) => onChange({ ...draft, [field]: value });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg overflow-hidden rounded-md bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0b1f3a]">
              {isPass ? (
                <ThumbsDown size={18} />
              ) : isContact ? (
                <Mail size={18} />
              ) : (
                <MessageSquareText size={18} />
              )}
              {isPass
                ? "Pass on candidate"
                : isContact
                ? "Record candidate contact"
                : "Express interest"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {draft.mode === "bulk"
                ? `${draft.count} selected candidates`
                : draft.candidateName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={17} />
          </button>
        </header>
        <div className="space-y-4 p-5">
          {!isPass && (
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-600">
                {isContact ? "Contact note" : "Candidate message"}
              </span>
              <textarea
                value={draft.customMessage}
                onChange={(event) =>
                  update("customMessage", event.target.value.slice(0, 1000))
                }
                rows={4}
                className={`${inputClass} h-auto resize-y py-2`}
                placeholder={
                  isContact
                    ? "Record the communication or intended outreach."
                    : "Optional message the candidate will receive with your interest."
                }
              />
              <span className="mt-1 block text-right text-xs text-slate-400">
                {draft.customMessage.length}/1000
              </span>
            </label>
          )}
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">
              {isPass ? "Reason for passing" : "Internal hiring note"}
            </span>
            <textarea
              value={draft.reason}
              onChange={(event) =>
                update("reason", event.target.value.slice(0, 500))
              }
              rows={3}
              className={`${inputClass} h-auto resize-y py-2`}
              placeholder={
                isPass
                  ? "Record a concise, job-related reason."
                  : "Optional note for your hiring team."
              }
            />
            <span className="mt-1 block text-right text-xs text-slate-400">
              {draft.reason.length}/500
            </span>
          </label>
        </div>
        <footer className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 rounded-md border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#0b1f3a] px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : isPass ? (
              <ThumbsDown size={16} />
            ) : isContact ? (
              <Mail size={16} />
            ) : (
              <Send size={16} />
            )}
            {isPass
              ? "Record pass"
              : isContact
              ? "Record contact"
              : "Send interest"}
          </button>
        </footer>
      </form>
    </div>
  );
};

const JobMatchesPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    currentJob,
    matchingCandidates,
    matchesSummary,
    matchesPagination,
    matchesDisplayPolicy,
    loading,
    error,
    matchingConfiguration,
    fetchMatchingConfiguration,
    fetchJobMatches,
    fetchMatchDetails,
    takeActionOnImmediateMatch,
    bulkMatchAction,
  } = useJobStore();
  const [filters, setFilters] = useState({
    minScore: 50,
    matchBand: "",
    matchStatus: "",
    workAuthorizationStatus: "",
    availability: "",
    sortBy: "match_score",
    sortOrder: "DESC",
    limit: 20,
    offset: 0,
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [workAuthorizationInput, setWorkAuthorizationInput] = useState("");
  const [advancedFilters, setAdvancedFilters] = useState(false);
  const [reviewLane, setReviewLane] = useState("all");
  const [selected, setSelected] = useState([]);
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [details, setDetails] = useState({});
  const [detailState, setDetailState] = useState({});
  const [actionDraft, setActionDraft] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [configurationApplied, setConfigurationApplied] = useState(false);
  const loadedDetails = useRef(new Set());

  useEffect(() => {
    fetchMatchingConfiguration();
  }, [fetchMatchingConfiguration]);
  useEffect(() => {
    if (configurationApplied || !matchingConfiguration?.preferences) return;
    setFilters((previous) => ({
      ...previous,
      minScore:
        matchingConfiguration.preferences.minimumScoreThreshold ??
        previous.minScore,
      limit:
        matchingConfiguration.preferences.immediateMatchLimit ?? previous.limit,
    }));
    setConfigurationApplied(true);
  }, [configurationApplied, matchingConfiguration]);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setFilters((previous) =>
          previous.search === searchInput.trim()
            ? previous
            : { ...previous, search: searchInput.trim(), offset: 0 }
        ),
      350
    );
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    const value = workAuthorizationInput.trim();
    const timer = setTimeout(
      () =>
        setFilters((previous) =>
          previous.workAuthorizationStatus === value
            ? previous
            : { ...previous, workAuthorizationStatus: value, offset: 0 }
        ),
      350
    );
    return () => clearTimeout(timer);
  }, [workAuthorizationInput]);

  const loadMatches = useCallback(async () => {
    const result = await fetchJobMatches(jobId, filters);
    if (!result.success) toast.error(result.error);
  }, [fetchJobMatches, filters, jobId]);
  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const openReview = useCallback(
    async (match) => {
      const key = matchKey(match);
      setActiveMatchId(match.matchId);
      if (loadedDetails.current.has(key)) return;
      setDetailState((previous) => ({
        ...previous,
        [key]: { loading: true, error: null },
      }));
      const result = await fetchMatchDetails(jobId, match.matchId);
      if (result.success) {
        loadedDetails.current.add(key);
        setDetails((previous) => ({ ...previous, [key]: result.match }));
        setDetailState((previous) => ({
          ...previous,
          [key]: { loading: false, error: null },
        }));
      } else {
        setDetailState((previous) => ({
          ...previous,
          [key]: { loading: false, error: result.error },
        }));
      }
    },
    [fetchMatchDetails, jobId]
  );

  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);
  const selectedMatches = useMemo(
    () =>
      matchingCandidates
        .filter((match) => selectedSet.has(String(match.matchId)))
        .slice(0, 4),
    [matchingCandidates, selectedSet]
  );
  const visibleMatches = useMemo(
    () =>
      matchingCandidates.filter((match) => {
        if (reviewLane === "unreviewed") return !match.employerAction;
        if (reviewLane === "candidate_interested")
          return (
            match.candidateAction === "interested" &&
            match.employerAction !== "interested"
          );
        if (reviewLane === "shortlisted")
          return match.employerAction === "interested";
        if (reviewLane === "mutual")
          return (
            match.matchStatus === "mutual" ||
            (match.employerAction === "interested" &&
              match.candidateAction === "interested")
          );
        return true;
      }),
    [matchingCandidates, reviewLane]
  );
  const laneCounts = useMemo(
    () => ({
      all: matchingCandidates.length,
      unreviewed: matchingCandidates.filter((match) => !match.employerAction)
        .length,
      candidate_interested: matchingCandidates.filter(
        (match) =>
          match.candidateAction === "interested" &&
          match.employerAction !== "interested"
      ).length,
      shortlisted: matchingCandidates.filter(
        (match) => match.employerAction === "interested"
      ).length,
      mutual: matchingCandidates.filter(
        (match) =>
          match.matchStatus === "mutual" ||
          (match.employerAction === "interested" &&
            match.candidateAction === "interested")
      ).length,
    }),
    [matchingCandidates]
  );
  const activeListMatch = matchingCandidates.find(
    (match) => String(match.matchId) === String(activeMatchId)
  );
  const activeMatch = activeMatchId
    ? details[String(activeMatchId)] || activeListMatch
    : null;
  const activeDetailState = detailState[String(activeMatchId)] || {};
  const limitedCalibrationCount = matchingCandidates.filter(
    (match) => match.decisionSupport?.calibration?.requiresManualReview
  ).length;

  const updateFilter = (field, value) => {
    setSelected([]);
    setFilters((previous) => ({ ...previous, [field]: value, offset: 0 }));
  };
  const toggleSelected = (matchId) =>
    setSelected((previous) =>
      previous.map(String).includes(String(matchId))
        ? previous.filter((id) => String(id) !== String(matchId))
        : [...previous, matchId]
    );
  const openAction = (match, action) =>
    setActionDraft({
      mode: "single",
      matchId: match.matchId,
      action,
      candidateName: candidateName(match),
      count: 1,
      customMessage: "",
      reason: "",
      notifyCandidate: action !== "not_interested",
    });
  const openBulkAction = (action) =>
    setActionDraft({
      mode: "bulk",
      matchIds: selected,
      action,
      count: selected.length,
      candidateName: "",
      customMessage: "",
      reason: "",
      notifyCandidate: action !== "not_interested",
    });

  const submitAction = async (event) => {
    event.preventDefault();
    if (!actionDraft) return;
    setActionLoading(true);
    const options = {
      notifyCandidate: actionDraft.action !== "not_interested",
      ...(actionDraft.reason.trim()
        ? { reason: actionDraft.reason.trim() }
        : {}),
      ...(actionDraft.action === "contacted"
        ? {
            reachOut: {
              useSharedContact: true,
              preferredContactMethod: "email",
              message: actionDraft.customMessage.trim(),
            },
          }
        : {}),
    };
    const result =
      actionDraft.mode === "bulk"
        ? await bulkMatchAction(jobId, {
            matchIds: actionDraft.matchIds.map(Number),
            action: actionDraft.action,
            customMessage: actionDraft.customMessage.trim(),
            ...options,
          })
        : await takeActionOnImmediateMatch(
            jobId,
            actionDraft.matchId,
            actionDraft.action,
            actionDraft.customMessage.trim(),
            options
          );
    if (result.success) {
      toast.success(
        actionDraft.action === "not_interested"
          ? "Candidate review recorded."
          : actionDraft.action === "contacted"
          ? "Candidate contact recorded."
          : "Interest recorded and candidate notified."
      );
      setActionDraft(null);
      setSelected([]);
      loadedDetails.current.clear();
      setDetails({});
      await loadMatches();
    } else toast.error(result.error);
    setActionLoading(false);
  };

  const openProfile = (match) =>
    navigate(
      `/candidates/${match.candidateId || match.candidate?.id}?jobId=${
        match.jobId || jobId
      }&matchId=${match.matchId}`
    );
  const openInterview = (match) =>
    navigate(
      `/interviews/schedule?matchId=${match.matchId}&jobId=${
        match.jobId || jobId
      }`
    );
  const openOffer = (match) =>
    navigate(
      `/offers/create?matchId=${match.matchId}&jobId=${
        match.jobId || jobId
      }&candidateId=${match.candidateId || match.candidate?.id}`
    );
  const pageTotal = Number(
    matchesPagination?.total ?? matchingCandidates.length
  );
  const offset = Number(matchesPagination?.offset || filters.offset || 0);
  const limit = Number(matchesPagination?.limit || filters.limit || 20);
  const hasFilters = Boolean(
    filters.search ||
      filters.matchBand ||
      filters.matchStatus ||
      filters.workAuthorizationStatus ||
      filters.availability ||
      Number(filters.minScore) > 0
  );

  return (
    <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px]">
        <button
          type="button"
          onClick={() => navigate(`/jobs/${jobId}`)}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#0b1f3a]"
        >
          <ArrowLeft size={16} /> Job details
        </button>
        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase text-sky-700">
              Candidate review
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-[#0b1f3a] sm:text-3xl">
              {currentJob?.title || "Job matches"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Compare job-relevant evidence, record a decision, and open the
              complete profile only when deeper review is needed.
            </p>
          </div>
          {matchesDisplayPolicy?.scoreIsAssistive && (
            <div className="inline-flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={15} className="text-sky-600" /> Scores support
              human review and do not make hiring decisions.
            </div>
          )}
        </div>

        <section className="mt-6 grid grid-cols-2 gap-4 rounded-md border border-slate-200 bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.035)] lg:grid-cols-4">
          <SummaryMetric
            icon={UsersRound}
            label="Matches"
            value={matchesSummary?.totalMatches ?? pageTotal}
            detail="Current result set"
          />
          <SummaryMetric
            icon={SlidersHorizontal}
            label="Average fit"
            value={`${matchesSummary?.averageScore || 0}%`}
            detail="Assistive score"
          />
          <SummaryMetric
            icon={Check}
            label="Shortlisted"
            value={matchesSummary?.employerInterested || 0}
            detail="Employer interested"
          />
          <SummaryMetric
            icon={Mail}
            label="Mutual"
            value={matchesSummary?.mutualMatches || 0}
            detail="Both parties interested"
          />
        </section>

        {limitedCalibrationCount > 0 && (
          <div className="mt-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              <strong>
                {limitedCalibrationCount === 1
                  ? "This result"
                  : `${limitedCalibrationCount} results`}{" "}
                need calibration-aware review.
              </strong>{" "}
              The job requirements or available evidence limit score certainty.
              Skills gaps and unscored fields are shown explicitly in each
              review.
            </div>
          </div>
        )}

        {matchingCandidates.length >= 3 && (
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-y border-slate-200 py-3 text-xs text-slate-500">
            <span>
              <strong className="text-[#0b1f3a]">
                {matchesSummary?.bandDistribution?.A || 0}
              </strong>{" "}
              strongest-fit candidates
            </span>
            <span>
              <strong className="text-[#0b1f3a]">
                {laneCounts.unreviewed}
              </strong>{" "}
              awaiting review
            </span>
            <span>
              <strong className="text-[#0b1f3a]">
                {laneCounts.candidate_interested}
              </strong>{" "}
              candidates interested
            </span>
          </div>
        )}

        <section className="mt-6 overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.035)]">
          <div className="border-b border-slate-200 p-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="relative w-full xl:max-w-md">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className={`${inputClass} pl-10`}
                  placeholder="Search candidate, skill, company or institution"
                  aria-label="Search candidate matches"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setAdvancedFilters((value) => !value)}
                  className={`inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium ${
                    advancedFilters
                      ? "border-sky-200 bg-sky-50 text-sky-700"
                      : "border-slate-200 text-slate-600"
                  }`}
                >
                  <Filter size={15} /> Filters{" "}
                  <ChevronDown
                    size={14}
                    className={advancedFilters ? "rotate-180" : ""}
                  />
                </button>
                <select
                  value={filters.sortBy}
                  onChange={(event) =>
                    updateFilter("sortBy", event.target.value)
                  }
                  className={`${inputClass} w-auto`}
                  aria-label="Sort matches"
                >
                  <option value="match_score">Best fit</option>
                  <option value="created_at">Newest match</option>
                  <option value="experience_years">Experience</option>
                  <option value="last_updated">Recently updated</option>
                </select>
              </div>
            </div>
            {advancedFilters && (
              <div className="mt-4 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-3">
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Minimum score: {filters.minScore}%
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={filters.minScore}
                    onChange={(event) =>
                      updateFilter("minScore", Number(event.target.value))
                    }
                    className="h-10 w-full accent-sky-600"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Match band
                  </span>
                  <select
                    value={filters.matchBand}
                    onChange={(event) =>
                      updateFilter("matchBand", event.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">All bands</option>
                    <option value="A">Band A</option>
                    <option value="B">Band B</option>
                    <option value="C">Band C</option>
                    <option value="D">Band D</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Workflow status
                  </span>
                  <select
                    value={filters.matchStatus}
                    onChange={(event) =>
                      updateFilter("matchStatus", event.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">All statuses</option>
                    <option value="strong_match">Strong match</option>
                    <option value="good_match">Good match</option>
                    <option value="potential">Potential</option>
                    <option value="employer_interested">
                      Employer interested
                    </option>
                    <option value="candidate_interested">
                      Candidate interested
                    </option>
                    <option value="mutual">Mutual interest</option>
                    <option value="employer_rejected">Passed</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Availability
                  </span>
                  <select
                    value={filters.availability}
                    onChange={(event) =>
                      updateFilter("availability", event.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">Any availability</option>
                    <option value="immediately">Immediately</option>
                    <option value="two_weeks">Within two weeks</option>
                    <option value="one_month">Within one month</option>
                    <option value="three_months">Within three months</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Work authorization
                  </span>
                  <input
                    value={workAuthorizationInput}
                    onChange={(event) =>
                      setWorkAuthorizationInput(event.target.value)
                    }
                    className={inputClass}
                    placeholder="e.g. UK work authorized"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-medium text-slate-500">
                    Page size
                  </span>
                  <select
                    value={filters.limit}
                    onChange={(event) =>
                      updateFilter("limit", Number(event.target.value))
                    }
                    className={inputClass}
                  >
                    <option value={10}>10 candidates</option>
                    <option value={20}>20 candidates</option>
                    <option value={50}>50 candidates</option>
                  </select>
                </label>
              </div>
            )}
          </div>

          <div
            className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 px-4"
            aria-label="Review lanes"
          >
            {[
              ["all", "All"],
              ["unreviewed", "Needs review"],
              ["candidate_interested", "Candidate interested"],
              ["shortlisted", "Shortlisted"],
              ["mutual", "Mutual"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setReviewLane(value);
                  setSelected([]);
                }}
                className={`h-11 whitespace-nowrap border-b-2 px-3 text-xs font-medium ${
                  reviewLane === value
                    ? "border-sky-600 text-sky-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {label}{" "}
                <span className="ml-1 text-[10px] text-slate-400">
                  {laneCounts[value]}
                </span>
              </button>
            ))}
          </div>

          {selected.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-sky-100 bg-sky-50 px-4 py-3">
              <span className="text-sm font-medium text-sky-900">
                {selected.length} selected
                {selected.length > 4 ? " · first 4 available to compare" : ""}
              </span>
              <div className="flex flex-wrap gap-2">
                {selected.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => setCompareOpen(true)}
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-sky-200 bg-white px-3 text-xs font-medium text-sky-700"
                  >
                    <UsersRound size={14} /> Compare
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openBulkAction("not_interested")}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-sky-200 bg-white px-3 text-xs font-medium text-slate-600"
                >
                  <ThumbsDown size={14} /> Pass
                </button>
                <button
                  type="button"
                  onClick={() => openBulkAction("interested")}
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-[#0b1f3a] px-3 text-xs font-medium text-white"
                >
                  <Check size={14} /> Shortlist
                </button>
              </div>
            </div>
          )}

          {loading && !matchingCandidates.length ? (
            <MatchSkeleton />
          ) : error && !matchingCandidates.length ? (
            <div className="m-4 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          ) : !matchingCandidates.length ? (
            <EmptyState filtered={hasFilters} />
          ) : !visibleMatches.length ? (
            <EmptyState filtered />
          ) : (
            <div className="divide-y divide-slate-100">
              {visibleMatches.map((match) => (
                <MatchRow
                  key={match.matchId}
                  match={match}
                  selected={selectedSet.has(String(match.matchId))}
                  onSelect={toggleSelected}
                  onReview={openReview}
                  onAction={openAction}
                />
              ))}
            </div>
          )}

          <footer className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing {pageTotal ? offset + 1 : 0}-
              {Math.min(offset + matchingCandidates.length, pageTotal)} of{" "}
              {pageTotal}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={loading || offset <= 0}
                onClick={() =>
                  setFilters((previous) => ({
                    ...previous,
                    offset: Math.max(0, offset - limit),
                  }))
                }
                className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 px-3 font-medium text-slate-600 disabled:opacity-40"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                type="button"
                disabled={
                  loading ||
                  !(
                    matchesPagination?.hasMore ||
                    offset + matchingCandidates.length < pageTotal
                  )
                }
                onClick={() =>
                  setFilters((previous) => ({
                    ...previous,
                    offset: offset + limit,
                  }))
                }
                className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 px-3 font-medium text-slate-600 disabled:opacity-40"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </footer>
        </section>
      </div>

      <MatchDrawer
        match={activeMatch}
        loading={activeDetailState.loading}
        error={activeDetailState.error}
        onClose={() => setActiveMatchId(null)}
        onAction={openAction}
        onProfile={openProfile}
        onInterview={openInterview}
        onOffer={openOffer}
      />
      <CompareDialog
        matches={compareOpen ? selectedMatches : []}
        onClose={() => setCompareOpen(false)}
        onReview={(match) => {
          setCompareOpen(false);
          openReview(match);
        }}
      />
      <ActionDialog
        draft={actionDraft}
        onChange={setActionDraft}
        onClose={() => setActionDraft(null)}
        onSubmit={submitAction}
        submitting={actionLoading}
      />
    </div>
  );
};

export default JobMatchesPage;
